import React, { ComponentProps, useCallback, useMemo, useState } from 'react';
import useAuth from 'auth/UseAuth';
import { Box, Button, CircularProgress, IconButton } from '@mui/material';
import { DownloadFile, FileDownloadState, FileDownloadStatus } from '../helpers/FileProcessing';
import SnackbarUtils from '../notifications/SnackbarUtils';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import DownloadDoneIcon from '@mui/icons-material/DownloadDone';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import assertExhaustive from '../researchProjectManagement/patientApproval/utils/assertExhaustive';

type ButtonProps = ComponentProps<typeof Button> & ComponentProps<typeof IconButton>;
type UserDefinedButtonProps = Omit<ButtonProps, 'onClick' | 'children'>;

export type DownloadButtonProps = {
  downloadData: (accessToken: string | undefined) => Promise<void>;
} & UserDefinedButtonProps;

// Common component for buttons that download a file. This component can be configured either as an icon-only button or
// as a regular button with user-defined content after the icon.
export const DownloadButton: React.FC<DownloadButtonProps> = ({ downloadData, children, ...buttonProps }) => {
  const { accessToken } = useAuth();

  const [downloadState, setDownloadState] = useState<FileDownloadState>({ status: 'NotStarted' });

  const updateDownloadState = useCallback((newState: FileDownloadState) => {
    setDownloadState(newState);
    if (newState.status === 'Error') {
      const errorMessage =
        newState.errorMessage && newState.errorMessage.length > 0 ? newState.errorMessage : 'An unknown error occurred';
      SnackbarUtils.error(`Download Error: ${errorMessage}`);
    }
  }, []);

  const doDownload = useCallback(() => {
    if (accessToken === undefined) {
      return;
    }

    DownloadFile(updateDownloadState, () => downloadData(accessToken));
  }, [accessToken, downloadData, updateDownloadState]);

  const finalButtonProps = useMemo(
    () =>
      ({
        ...buttonProps,
        onClick: doDownload,
        disabled: buttonProps.disabled || downloadState.status === 'Downloading',
      } as ButtonProps),
    [buttonProps, doDownload, downloadState]
  );

  return children ? (
    <Button {...finalButtonProps}>
      <StatusIcon status={downloadState.status} />
      {children}
    </Button>
  ) : (
    <IconButton {...finalButtonProps}>
      <StatusIcon status={downloadState.status} />
    </IconButton>
  );
};

const StatusIcon: React.FC<{ status: FileDownloadStatus }> = ({ status }) => {
  switch (status) {
    case 'NotStarted':
      return <SaveAltIcon />;
    case 'Downloading':
      return (
        <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      );
    case 'Complete':
      return <DownloadDoneIcon />;
    case 'Error':
      return <ErrorOutlineIcon />;
    default:
      assertExhaustive(status);
      throw new Error('Unreachable');
  }
};
